/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { IIPInterface } from "../../../store/api/deviceConfig/types";
import { useSelector } from "react-redux";
import { selectedLanDeviceSelector } from "../../../store/api/deviceConfig/selectors";
import { useIPInterfaceData } from "./hooks";

interface IAddEditIPInterfaceProps {
  modelOpenMode?: string;
  selectedIPInterface: IIPInterface;
}

const AddEditIPInterface: React.FC<IAddEditIPInterfaceProps> = ({ modelOpenMode = "Add", selectedIPInterface }) => {
  const dispatch = useStoreDispatch();
  const selectedLanDevice = useSelector(selectedLanDeviceSelector);
  const { ipInterfaceData, setIPInterfaceData, isAddNewIPInterface, submitHandler, errMsg, setErrMsg, blurHandler } =
    useIPInterfaceData(selectedIPInterface);

  const handleInputChange = (name: string, value: string) => {
    setIPInterfaceData({ ...ipInterfaceData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleCheckboxChange = (name: string) => {
    const key = name as keyof IIPInterface;
    setIPInterfaceData({ ...ipInterfaceData, [key]: ipInterfaceData?.[key] === "true" ? "false" : "true" });
  };

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const isEnableSave =
    !!ipInterfaceData.ipInterfaceIpAddress &&
    !!ipInterfaceData.ipInterfaceSubnetMask &&
    !!ipInterfaceData.egressQosGroup &&
    !!ipInterfaceData.forwardingGroupIndex &&
    !(Object.values(errMsg).filter((item) => !!item && item).length > 0);

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button disabled={!isEnableSave} onClick={submitHandler}>
        Save
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title={`${isAddNewIPInterface ? "Add" : "Edit"} IP Interface`} footer={footer} width="520px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Checkboxes */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="enable-ip-interface"
                checked={ipInterfaceData?.enable === "true"}
                onCheckedChange={() => handleCheckboxChange("enable")}
              />
              <Label htmlFor="enable-ip-interface" className="cursor-pointer">Enable IP Interface</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="dhcp-server-enable"
                checked={ipInterfaceData?.dhcpServerEnable === "true"}
                onCheckedChange={() => handleCheckboxChange("dhcpServerEnable")}
              />
              <Label htmlFor="dhcp-server-enable" className="cursor-pointer">DHCP Server Enable</Label>
            </div>
          </div>

          {/* Index (hidden) */}
          <div className="hidden">
            <Label htmlFor="ip-index">Index</Label>
            <Input
              id="ip-index"
              name="index"
              disabled={!isAddNewIPInterface}
              value={ipInterfaceData?.index || selectedLanDevice?.ipInterfaces.length + 1}
              onChange={(e) => handleInputChange("index", e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="ip-description">Description</Label>
            <Input
              id="ip-description"
              name="description"
              value={ipInterfaceData?.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              onBlur={blurHandler}
            />
            {errMsg?.description && <p className="text-sm text-destructive">{errMsg.description}</p>}
          </div>

          {/* IP Address */}
          <div className="space-y-2">
            <Label htmlFor="ip-address">
              IP Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ip-address"
              name="ipInterfaceIpAddress"
              value={ipInterfaceData?.ipInterfaceIpAddress}
              onChange={(e) => handleInputChange("ipInterfaceIpAddress", e.target.value)}
              onBlur={blurHandler}
            />
            {errMsg?.ipInterfaceIpAddress && <p className="text-sm text-destructive">{errMsg.ipInterfaceIpAddress}</p>}
          </div>

          {/* Subnet Mask */}
          <div className="space-y-2">
            <Label htmlFor="ip-subnet-mask">
              Subnet Mask <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ip-subnet-mask"
              name="ipInterfaceSubnetMask"
              value={ipInterfaceData?.ipInterfaceSubnetMask}
              onChange={(e) => handleInputChange("ipInterfaceSubnetMask", e.target.value)}
              onBlur={blurHandler}
            />
            {errMsg?.ipInterfaceSubnetMask && <p className="text-sm text-destructive">{errMsg.ipInterfaceSubnetMask}</p>}
          </div>

          {/* VLAN ID */}
          <div className="space-y-2">
            <Label htmlFor="ip-vlan-id">VLAN ID</Label>
            <Input
              id="ip-vlan-id"
              name="vlanId"
              disabled
              value={ipInterfaceData?.vlanId}
              onChange={(e) => handleInputChange("vlanId", e.target.value)}
              onBlur={blurHandler}
            />
          </div>

          {/* Forwarding Group Index */}
          <div className="space-y-2">
            <Label htmlFor="ip-forwarding-group">
              Forwarding Group Index <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ip-forwarding-group"
              name="forwardingGroupIndex"
              value={ipInterfaceData?.forwardingGroupIndex}
              onChange={(e) => handleInputChange("forwardingGroupIndex", e.target.value)}
              onBlur={blurHandler}
            />
            {errMsg?.forwardingGroupIndex && <p className="text-sm text-destructive">{errMsg.forwardingGroupIndex}</p>}
          </div>

          {/* Egress Qos Profile */}
          <div className="space-y-2">
            <Label htmlFor="ip-egress-qos">
              Egress Qos Profile <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ip-egress-qos"
              name="egressQosGroup"
              value={ipInterfaceData?.egressQosGroup}
              onChange={(e) => handleInputChange("egressQosGroup", e.target.value)}
              onBlur={blurHandler}
            />
            {errMsg?.egressQosGroup && <p className="text-sm text-destructive">{errMsg.egressQosGroup}</p>}
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditIPInterface;
